/**
 * CSV Upload component with drag-and-drop
 */

import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useImportCSV } from '@/lib/api/hooks/useProspects';
import { cn } from '@/lib/utils';

export function CSVUpload() {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const importCSV = useImportCSV();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Filter for CSV files only
    const csvFiles = acceptedFiles.filter(
      (file) => file.type === 'text/csv' || file.name.endsWith('.csv')
    );
    setSelectedFiles(csvFiles);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: true,
  });

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      await importCSV.mutateAsync(selectedFiles);
      setSelectedFiles([]);
    } catch (error) {
      // Error is already handled by React Query
      console.error('Upload error:', error);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((files) => files.filter((_, i) => i !== index));
  };

  const result = importCSV.data;

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <Card
        {...getRootProps()}
        className={cn(
          'border-2 border-dashed p-8 text-center cursor-pointer transition-colors',
          isDragActive && 'border-primary bg-primary/5',
          !isDragActive && 'hover:border-primary/50'
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-2">
          <Upload className="h-10 w-10 text-muted-foreground" />
          {isDragActive ? (
            <p className="text-sm text-muted-foreground">Drop CSV files here...</p>
          ) : (
            <>
              <p className="text-sm font-medium">Drag & drop CSV files here</p>
              <p className="text-xs text-muted-foreground">
                or click to select files
              </p>
            </>
          )}
        </div>
      </Card>

      {/* Selected files */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
          </p>
          {selectedFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-muted rounded-md"
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{file.name}</span>
                <span className="text-xs text-muted-foreground">
                  ({(file.size / 1024).toFixed(1)} KB)
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveFile(index)}
              >
                Remove
              </Button>
            </div>
          ))}
          <Button
            onClick={handleUpload}
            disabled={importCSV.isPending}
            className="w-full"
          >
            {importCSV.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      )}

      {/* Success result */}
      {importCSV.isSuccess && result && (
        <Alert className="border-green-500">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertDescription>
            <div className="space-y-1">
              <p className="font-medium">Import successful!</p>
              <p className="text-sm">
                Processed {result.files_processed} file{result.files_processed > 1 ? 's' : ''},{' '}
                {result.successful} prospect{result.successful > 1 ? 's' : ''} imported
                {result.failed > 0 && `, ${result.failed} failed`}
              </p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Errors */}
      {importCSV.isSuccess && result && result.errors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-medium">Import errors:</p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {result.errors.slice(0, 10).map((error, index) => (
                  <p key={index} className="text-xs">
                    {error.file} (row {error.row}): {error.error}
                  </p>
                ))}
                {result.errors.length > 10 && (
                  <p className="text-xs italic">
                    ...and {result.errors.length - 10} more errors
                  </p>
                )}
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* API Error */}
      {importCSV.isError && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            {importCSV.error.message || 'Failed to import CSV'}
          </AlertDescription>
        </Alert>
      )}

      {/* Help text */}
      <div className="text-xs text-muted-foreground space-y-1">
        <p className="font-medium">CSV Format Requirements:</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>
            Required column: <code className="bg-muted px-1 rounded">phone</code> (or
            phone_number, tel, telephone)
          </li>
          <li>
            Optional columns: company_name, name, email, website, city, state, notes
          </li>
          <li>Any additional columns will be saved as extra fields</li>
        </ul>
      </div>
    </div>
  );
}
